// janus-engine/reactions/counterspell-rules.ts — lane RX S6 (2026-09-28): the counterspell RULE LIBRARY, pure twins of
// includes/functions/janus/counterspell_inc.php. The dispatch itself stays PHP (template_spell_roll.php is still the legacy cast lane,
// so the interrupt-window concept's IP3 cannot open yet); these rules are what a client-truth pre-cast scan will call.
// Where PHP throws (a TypeError on an unexpected shape, CastingTime::get on an unknown key) the twin throws TriggerInputUnavailable.
// Parity: tools/probes/rx_trigger_oracle_capture.php (unit.cs_*) → tools/test/janus-rx-trigger-dispatch.test.mjs.
import { TriggerInputUnavailable, abilityEntryId, type Row, type TriggerReaders } from './trigger-dispatch.js';

const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const own = (obj: object, key: string): boolean => Object.prototype.hasOwnProperty.call(obj, key);
type Read = Pick<TriggerReaders, 'entries' | 'value' | 'itemField' | 'definition'>;
/** A PHP array — JSON carries a list as an array and a keyed/gapped one as an object. */
const phpArray = (v: unknown): v is unknown[] | Row => Array.isArray(v) || record(v) !== null;
/** PHP `$arr[0]` on either JSON shape (undefined when absent). */
const first = (v: unknown): unknown => Array.isArray(v) ? v[0] : record(v) ? record(v)!['0'] : undefined;

/** CastingTime::IDS — an unknown key THROWS in PHP (HasIds::get), so it is unavailable here. */
const CASTING_TIME_AP: Record<string, number | 'r'> = { '3ap': 3, '2ap': 2, '1ap': 1, reaction: 'r', one_minute: 3, ten_minutes: 3,
  one_hour: 3, eight_hours: 3, twelve_hours: 3, twenty_four_hours: 3 };

/** creature_knows_spell_id(): spells_known (a bare id or a row's item id), at-will / innate carriers, the RAW prepared list. */
export function creatureKnowsSpellId(read: Read, creature: Row, spellId: string): boolean {
  if (!/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(spellId)) return false;
  const has = (row: Row, key: string): boolean => read.value(row, key) != null || read.entries(row, key).length > 0;
  if (has(creature, 'spells_known')) {
    for (const row of read.entries(creature, 'spells_known')) {
      if (typeof row === 'string') { if (row === spellId) return true; continue; }
      const r = record(row); if (!r) continue;
      const id = read.itemField(r, 'item_id') ?? r.item_id ?? first(r.item_id_used) ?? '';
      if (id === spellId) return true;
    }
  }
  for (const layer of ['at_will_spell', 'innate_spell_per_long_rest']) {
    for (const entry of read.entries(creature, layer)) {
      const e = record(entry); if (!e) continue;
      if (abilityEntryId(read as TriggerReaders, e) === spellId) return true;
      // PHP then calls item_field($entry['item_arr']): a STRING item_arr is a TypeError there.
      if (e.item_arr !== undefined && e.item_arr !== null && !phpArray(e.item_arr)) throw new TriggerInputUnavailable('counterspell:scalar-item-arr');
      const nested = record(e.item_arr);
      if ((read.itemField(nested, 'item_id') ?? first(nested?.item_id_used) ?? '') === spellId) return true;
    }
  }
  if (has(creature, 'spell_prepared_1') && phpArray(creature.spell_prepared_1)) {
    for (const entry of read.entries(creature, 'spell_prepared_1')) {
      const sid = phpArray(entry) ? first(entry) ?? '' : entry;
      if (sid === spellId) return true;
    }
  }
  return false;
}

/** counterspell_spell_casting_time_eligible(): only a 2+ AP cast (or a ritual-length one) can trip an armed watch. */
export function counterspellCastingTimeEligible(read: Read, spell: Row): boolean {
  const raw = read.value(spell, 'spell_casting_time') ?? '3ap';
  if (raw === '1ap' || raw === 'reaction') return false;
  if (typeof raw !== 'string' && typeof raw !== 'number') throw new TriggerInputUnavailable('counterspell:casting-time-shape');
  const ct = String(raw);
  if (!own(CASTING_TIME_AP, ct)) throw new TriggerInputUnavailable(`counterspell:unknown-casting-time:${ct}`);
  const ap = CASTING_TIME_AP[ct]!;
  return ap !== 'r' && ap >= 2;
}

/** PHP 8 `(int)` on a layer value: a leading-numeric string's number, a float truncated (non-finite / out of int64 range → 0),
 *  true → 1, anything else → 0. */
function phpInt(v: unknown): number {
  let n: number;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string') { const m = /^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?/.exec(v); n = m ? Number(m[0]) : 0; }
  else return 0;
  if (!Number.isFinite(n) || Math.abs(n) >= 9.2e18) return 0;
  return Math.trunc(n);
}
/** counterspell_item_tier(): (int) counterspell_tier ?? 1, clamped to 1..3. */
export function counterspellItemTier(read: Read, item: Row): number {
  const raw = read.value(item, 'counterspell_tier');
  return Math.max(1, Math.min(3, raw === null || raw === undefined ? 1 : phpInt(raw)));
}

/** counterspell_lock_status_for_school(): circle / sphere → its lockout status (StatusEffect ids, read from PHP by the oracle). The
 *  lookup is CASE-SENSITIVE like PHP's array key. */
const SCHOOL_LOCK: Record<string, string> = {
  'DD1EFCA2-2A4C-AD3D-B814-A325B2D22D85': '2A64D1E8-7C3B-4F92-B5A8-E19D64C72F35', '54058786-9840-7A02-90F6-36151FA0A3F2': '9E15B7A2-3D48-4C61-A927-F84B21D6E593',
  '9931FE30-8ACF-40E2-8E69-EB78925364DF': '6C82F394-1A5D-4E76-8B34-D29E75A1C648', '5886822F-FA79-E5CA-C255-DCC7D375CB52': '4F37A8D1-9B62-4A15-C783-28E91B5F4D76',
  'A28D2D20-BCE9-1CE0-4371-7F42231E9C43': '8D21C6F5-4E93-4B27-A164-73F8D2E9B5A4', 'EC1F7FF4-9E4C-DAE0-633A-B0BE7F4A2788': '3B96E4A7-2F51-4D38-9C82-46A1E7D3F925',
  'F5D2429C-6BA8-901F-2E23-30C780107C47': '7A45D2B9-6C18-4F63-B591-82D4F6A3E1C7', 'CC4C52BD-A341-0CAC-6C0B-671C9CC1C2EC': '5E63B1C4-8A27-4D95-A348-91C6E2F7D8B3',
  '96E272A4-8DDE-443C-0FCF-3A24ECFFB7C2': '1D87F5A3-6B42-4C19-8E75-34A9D1B6C2F8', '57F117E8-6659-4F2D-AED2-82D6005492B9': '9C34A6E1-5D78-4B26-A493-67F2E8D5B1A9',
  'B4D1AA5E-3B7B-4CE6-BA1E-DC43A5EEBF38': '6F92C3D7-1E85-4A64-B237-58D1A9F4E6C3',
};
export function counterspellLockStatusForSchool(schoolId: string): string | null { return own(SCHOOL_LOCK, schoolId) ? SCHOOL_LOCK[schoolId]! : null; }
